import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../context/auth';
import { Button } from '../components/common/Button';
import { InputGroup } from '../components/common/InputGroup';
import { validatePassword } from '../utils/validation';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { getAuthErrorTranslationKey } from '../utils/authErrors';
import { establishRecoverySession, isUnusableRecoverySessionError } from '../utils/recoverySession';
import { LoadingSpinner } from '../components/LoadingSpinner';

type SessionStatus = 'checking' | 'ready' | 'invalid_link' | 'error';

const PasswordReset = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { updateResetPassword } = useAuth();
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('checking');
  const [submitting, setSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    confirmPassword?: string;
  }>({});
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initSession = async () => {
      const result = await establishRecoverySession();
      if (cancelled) return;

      if (result.status === 'ready') {
        setSessionStatus('ready');
        return;
      }

      if (result.status === 'invalid_link') {
        setSessionStatus('invalid_link');
        return;
      }

      setSessionStatus('error');
      console.error('Failed to establish recovery session:', result.message);
    };

    void initSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validatePassword(confirmPassword);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    } else {
      setPasswordErrors([]);
    }
    if (!newPassword || !confirmPassword) {
      toast.error(t('errors.createAccount', { ns: 'auth' }));
      return;
    }

    setSubmitting(true);
    try {
      await updateResetPassword(confirmPassword);
      navigate('/login');
    } catch (err) {
      if (isUnusableRecoverySessionError(err)) {
        setSessionStatus('invalid_link');
        return;
      }
      toast.error(t(getAuthErrorTranslationKey(err, 'resetPassword.updateError'), { ns: 'auth' }));
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (sessionStatus === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (sessionStatus === 'invalid_link' || sessionStatus === 'error') {
    return (
      <div className="flex flex-col py-14 md:min-h-[60vh] md:justify-center md:items-center">
        <div className="w-full max-w-md text-center space-y-4">
          <h2 className="text-4xl font-bold text-secondary mb-4">
            {sessionStatus === 'invalid_link'
              ? t('resetPassword.expiredTitle')
              : t('resetPassword.title')}
          </h2>
          <p className="text-secondary">
            {sessionStatus === 'invalid_link'
              ? t('resetPassword.invalidLink')
              : t('resetPassword.sessionError')}
          </p>
          <Link
            to="/forgot-password"
            className="inline-block text-secondary font-ambit text-base font-bold underline underline-offset-2"
          >
            {t('resetPassword.requestNewLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col py-14 md:min-h-[60vh] md:justify-center md:items-center">
      <div className="w-full max-w-md">
        <h2 className="text-4xl font-bold text-secondary mb-4">{t('resetPassword.title')}</h2>
        <form className="w-full my-6 space-y-4" onSubmit={handleSubmit}>
          <ul className="list-disc list-outside my-4 pl-10 text-secondary space-y-2">
            <li>{t('errors.passwordTooShort', { ns: 'auth' })}</li>
            <li>{t('errors.passwordNumberOrSpecial', { ns: 'auth' })}</li>
            <li>{t('errors.passwordUpperLower', { ns: 'auth' })}</li>
          </ul>

          <div className="relative">
            <InputGroup
              label={t('resetPassword.newPassword')}
              type={show ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={t('passwordPlaceholder')}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-2/3 transform -translate-y-1/2 text-secondary-light"
              onClick={() => setShow(v => !v)}
              tabIndex={-1}
            >
              {show ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <InputGroup
                  label={t('resetPassword.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) {
                      setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                    }
                  }}
                  placeholder={t('passwordPlaceholder')}
                  required
                  error={passwordErrors.length > 0}
                  errorMessage={errors.confirmPassword}
                />
              </div>
              {passwordErrors.length > 0 && <AlertCircle className="text-red-500 w-5 h-5 mt-6" />}
            </div>

            {passwordErrors.length > 0 && (
              <ul className="mt-2 text-sm text-red-600 space-y-2 pl-2">
                {passwordErrors.map((err, idx) => (
                  <li key={idx} className="flex items-start">
                    {t(`errors.${err}`, { ns: 'auth' })}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting || !newPassword || !confirmPassword}
            className="w-full"
          >
            {submitting ? t('saving') : t('resetPassword.save')}
          </Button>
        </form>

        <div className="flex justify-center">
          <Link
            to="/login"
            className="text-secondary font-ambit text-base font-bold underline underline-offset-2"
          >
            {t('backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
